'use client';

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import InputText from '@/components/input/input-text';
import TextArea from '@/components/input/text-area';
import ErrorText from '@/components/typography/error-text';
import { Item } from '@/helper/types';
import InputNumber from '@/components/input/input-number';
import { itemsClient } from '@/app/clients/items.client';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
import PlusIcon from '@heroicons/react/24/outline/PlusIcon';
import TrashIcon from '@heroicons/react/24/outline/TrashIcon';

interface Props {
  closeModal: () => void;
  extraObject: any;
}

interface Extra {
  name: string;
  price: number;
}

const INITIAL_ITEM_OBJ: Item = {
  name: '',
  description: '',
  price: 0,
  category: '',
  imageUrl: '',
  inStock: true,
  calories: 0,
};

function AddItemModalBody({ closeModal }: Props) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showAddItemToast, setShowAddItemToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [itemObj, setItemObj] = useState<Item>(INITIAL_ITEM_OBJ);
  const [extras, setExtras] = useState<Extra[]>([]);
  const notify = () => toast.success('Item created successfully!');

  const handleAddExtra = () => {
    setExtras([...extras, { name: '', price: 0 }]);
  };

  const handleRemoveExtra = (index: number) => {
    setExtras(extras.filter((_, i) => i !== index));
  };

  const handleExtraChange = (
    index: number,
    field: 'name' | 'price',
    value: string | number
  ) => {
    const newExtras = [...extras];
    newExtras[index] = {
      ...newExtras[index],
      [field]: field === 'price' ? parseFloat(value as string) || 0 : value,
    };
    setExtras(newExtras);
  };

  async function saveNewItem() {
    if (itemObj.name.trim() === '') return setErrorMessage('Name is required!');
    else if (itemObj.description.trim() === '')
      return setErrorMessage('Description is required!');
    else if (itemObj.price === 0 || isNaN(itemObj.price))
      return setErrorMessage('Price is required!');
    else if (itemObj.category.trim() === '')
      return setErrorMessage('Category is required!');
    else if (itemObj.imageUrl.trim() === '')
      return setErrorMessage('Image url is required!');
    else {
      // Filter out extras with empty names
      const validExtras = extras.filter((extra) => extra.name.trim() !== '');

      const newItemObj: any = {
        name: itemObj.name,
        price: itemObj.price,
        category: itemObj.category,
        description: itemObj.description,
        imageUrl: itemObj.imageUrl,
        inStock: itemObj.inStock,
      };

      // Only add extras if there are valid ones
      if (validExtras.length > 0) {
        newItemObj.extras = validExtras;
      }

      const response = await itemsClient.post(newItemObj);
      console.log('Response:', response);
      if (response.status === 201) {
        notify();
        closeModal();
      }
    }
  }

  const updateFormValue = (updateType: string, value: any) => {
    setErrorMessage('');
    setItemObj({ ...itemObj, [updateType]: value });
  };

  return (
    <>
      <InputText
        type="text"
        defaultValue={itemObj.name}
        updateType="name"
        containerStyle="mt-4"
        labelTitle="Name"
        updateFormValue={updateFormValue}
      />

      <InputNumber
        type="number"
        defaultValue={itemObj.price}
        updateType="price"
        containerStyle="mt-4"
        labelTitle="Price"
        updateFormValue={updateFormValue}
      />

      <InputText
        type="text"
        defaultValue={itemObj.category}
        updateType="category"
        containerStyle="mt-4"
        labelTitle="Category"
        updateFormValue={updateFormValue}
      />

      <TextArea
        defaultValue={itemObj.description}
        updateType="description"
        containerStyle="mt-4"
        labelTitle="Description"
        updateFormValue={updateFormValue}
      />

      <InputText
        type="text"
        defaultValue={itemObj.imageUrl}
        updateType="imageUrl"
        containerStyle="mt-4"
        labelTitle="Image Url"
        updateFormValue={updateFormValue}
      />

      {/* Extras Section */}
      <div className="form-control w-full mt-4">
        <div className="flex justify-between items-center mb-2">
          <label className="label">
            <span className="label-text font-semibold">Extras (Optional)</span>
          </label>
          <button
            type="button"
            className="btn btn-xs btn-primary"
            onClick={handleAddExtra}
          >
            <PlusIcon className="w-4 h-4" />
            Add Extra
          </button>
        </div>

        {extras.length > 0 ? (
          <div className="space-y-2 p-3 bg-base-200 rounded-lg">
            {extras.map((extra, index) => (
              <div key={index} className="flex gap-2 items-center">
                <input
                  type="text"
                  value={extra.name}
                  onChange={(e) =>
                    handleExtraChange(index, 'name', e.target.value)
                  }
                  placeholder="Extra name (e.g., Extra Cheese)"
                  className="input input-sm input-bordered flex-1"
                />
                <div className="flex items-center gap-1">
                  <span className="text-sm">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={extra.price}
                    onChange={(e) =>
                      handleExtraChange(index, 'price', e.target.value)
                    }
                    placeholder="0.00"
                    className="input input-sm input-bordered w-24"
                  />
                </div>
                <button
                  type="button"
                  className="btn btn-square btn-sm btn-ghost text-error"
                  onClick={() => handleRemoveExtra(index)}
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500 italic p-3 bg-base-200 rounded-lg">
            No extras added yet. Click &quot;Add Extra&quot; to add options like
            extra cheese, extra sauce, etc.
          </div>
        )}
      </div>

      <ErrorText styleClass="mt-16">{errorMessage}</ErrorText>
      <div className="modal-action">
        <button className="btn btn-ghost" onClick={() => closeModal()}>
          Cancel
        </button>
        <button className="btn btn-primary px-6" onClick={() => saveNewItem()}>
          Save
        </button>
      </div>
      <div className="toast toast-top toast-end"></div>
    </>
  );
}

export default AddItemModalBody;
